<template>
  <main class="report-treatment-plans">
    <h1 class="report-treatment-plans__header">
      Планы лечения
    </h1>
    <form
      class="report-treatment-plans__filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <date-range-with-presets
        v-bind:presets="presetsOfDates"
        v-bind:value="selectedDates"
        custom-color-date="black"
        v-on:input="onInputDates" />
      <multiple-autocomplete-input
        label="Выберите врача"
        multiple
        removal-per-chip
        select-all-text="Все сотрудники"
        set-select-all-option
        small-chips
        v-bind:items="employeeOptions"
        v-bind:value="selectedEmployees"
        v-on:input="onInputEmployees"
      />
      <multiple-autocomplete-input
        label="Выберите ответственного"
        multiple
        removal-per-chip
        select-all-text="Все сотрудники"
        set-select-all-option
        small-chips
        v-bind:items="employeeOptions"
        v-bind:value="selectedResponsibleEmployees"
        v-on:input="onInputResponsibleEmployees"
      />
    </form>
    <button
      type="button"
      v-on:click="fetchReportSpreadsheet"
      class="report-treatment-plans__button">EXCEL</button>
    <div class="report-treatment-plans__body">
      <table class="report-treatment-plans-table report-treatment-plans__table">
        <thead class="report-treatment-plans-table__head-line">
          <tr>
            <th
              class="report-treatment-plans-table__head-cell"
              v-bind:key="field"
              v-for="({ field, title, sorts }) in reportColumns"
            >
              <sort-by-column
                class="report-treatment-plans-table__sort"
                v-bind:items="sorts"
                v-bind:label="title"
                v-bind:value="sort"
                v-if="sorts.length"
                v-on:input="onInputSort" />
              <template v-else>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="report-treatment-plans-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="(row, rowIndex) in reportRows"
          >
            <td
              class="report-treatment-plans-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in row"
            >
              <span>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import saveFileMixin from '@/mixins/saveFileMixin';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.title}`;
    return createElement('label', { attrs: { title: this.headTitle }, class: !this.isNotSelected ? 'selected' : '' }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.label}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * @description Компонент отчета по планам лечения.
 * @type {Vue}
 */
export default {
  components: {
    DateRangeWithPresets,
    MultipleAutocompleteInput,
    SortByColumn,
  },
  computed: {
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .presetsOfDates
          .slice(0, 1)
          .forEach((candidate) => {
            this.selectedDates = candidate.toValue();
          });
        this.selectedEmployees = this.employeeOptions.map(({ value }) => value);
        this.fetchReport();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      isFinishedReport: false,
      hasFetchedOptions: false,
      employeeOptions: [],
      responsibleEmployeeOptions: [],
      periodOptions: [],
      reportColumns: [],
      reportId: null,
      reportRows: [],
      sort: 'by-percent-of-completed-asc',
      selectedDates: [],
      selectedEmployees: [],
      employeeId: null,
      selectedResponsibleEmployees: [],
    };
  },
  methods: {
    debounceFetchReport: debounce(function _() { this.fetchReport(); }, 1000),
    /**
     * @description Получить опции отчета.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$reportGateway
        .getTreatmentPlansReportOptions()
        .then((result) => {
          result.forEach(({ doctors, employees, period_types: periods }) => {
            this.employeeOptions = employees;
            this.responsibleEmployeeOptions = doctors;
            this.periodOptions = periods;
          });
        });
    },
    /**
     * @description Получить данные отчета.
     * @returns {void}
     */
    fetchReport() {
      this.isFinishedReport = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$reportGateway
        .getTreatmentPlansReport(
          dateFrom,
          dateTo,
          this.selectedEmployees,
          this.sort,
          this.selectedResponsibleEmployees,
        )
        .then((result) => {
          result.forEach(({ columns, report_id: reportId, rows }) => {
            this.reportColumns = columns;
            this.reportId = reportId;
            this.reportRows = rows;
          });
        })
        .finally(() => { this.isFinishedReport = true; });
    },
    /**
     * @description Получить отчет в виде таблицы.
     * @returns {void}
     */
    fetchReportSpreadsheet() {
      const onSuccess = (response) => this
        .saveFile(response, 'Отчет по планам лечения.xls');
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$reportGateway
        .getTreatmentPlansReportBySheet(this.reportId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать выбор периода для отчета.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.debounceFetchReport();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать ввод сотрудников.
     * @param {string[]} value
     * @returns {void}
     */
    onInputEmployees(value) {
      this.selectedEmployees = value;
      this.debounceFetchReport();
    },
    /**
     * @description Обработать ввод ответственных сотрудников.
     * @param {string[]} value
     * @returns {void}
     */
    onInputResponsibleEmployees(value) {
      this.selectedResponsibleEmployees = value;
      this.debounceFetchReport();
    },
    /**
     * @description Обработать ввод сортировки.
     * @param {string} value
     * @returns {void}
     */
    onInputSort(value) {
      this.sort = value;
      this.debounceFetchReport();
    },
  },
  mixins: [notifierMixin, saveFileMixin],
  name: 'ClinicPanelReportTreatmentPlans',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.report-treatment-plans {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: scroll;
  padding: 2rem 1rem;
  width: 100%;
  &__filters {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    width: 100%;
  }
  &-table {
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    width: 100%;
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: nowrap;
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
    }
    &__head-cell {
      padding: 10px 8px;
      text-align: left;
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
    &__sort {
      cursor: pointer;
      input {
        display: none;
      }
      &::after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    margin: 40px 0 30px auto;
    flex-shrink: 0;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
}
@media screen and (max-width: 1380px) {
  .report-treatment-plans {
    &__body {
      overflow: scroll;
    }
    &-table {
      width: 1380px;
    }
  }
}
@media screen and (max-width: 1260px) {
  .report-treatment-plans {
    &__filters {
      flex-direction: column;
    }
  }
}
</style>
